/**
 * ReviewerCoveragePanel.jsx — Employer v1.1 Phase 1.0 follow-up
 * (loop-close v1, Part A).
 *
 * Read-only sidebar card surfaced on Role Detail. Summarises
 * reviewer coverage for a single role:
 *   - count of ACTIVE reviewer assignments on the role
 *   - count of OPEN candidate reviews on the role, split by status
 *     (pending, in_review)
 *
 * Strictly read-only — no writes. Two parallel direct Supabase JS
 * reads, both gated by admin/recruiter RLS (migration 099). UI
 * itself is also gated server-side: callers without admin/recruiter
 * RLS will simply see empty lists. We do not surface a per-role
 * picker, balancing logic, or per-reviewer breakdown — those are
 * out of scope for the audited safe surface.
 *
 * NO orchestration is invented. NO backend changes. NO new endpoint.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Users, RefreshCw, AlertCircle, ArrowRight } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import {
  listReviewerAssignments,
  listOpenReviewsByRole,
  listDecidedReviewsByRole,
} from '@/lib/employerReviewerRoster';
import { t } from '@/lib/employerReviewerStrings';


/**
 * Client-side mirror of `_classify_consensus` from
 * `routes/employer_governance_packs.py`. We do NOT import or wire
 * the backend classifier — the pack carries the authoritative
 * consensus when finalised. This is a calm inline summary for the
 * Role Detail surface, computed entirely from RLS-visible reviewer
 * rows. Mirror keys match the backend doctrine exactly so the chip
 * cannot drift from the frozen pack value.
 */
function classifyConsensus({ routed, decided, advance, reject }) {
  if (!routed)                return 'no_reviewers';
  if (decided < routed)       return 'incomplete';
  if (decided === advance)    return 'all_agree';
  if (decided === reject)     return 'all_agree';
  if (advance > decided / 2)  return 'majority_advance';
  if (reject  > decided / 2)  return 'majority_reject';
  return 'split';
}

const CONSENSUS_LABEL = {
  no_reviewers:     'Awaiting reviewer routing',
  incomplete:       'Awaiting additional review',
  all_agree:        'Consensus reached',
  majority_advance: 'Majority advance',
  majority_reject:  'Majority reject',
  split:            'Split review',
};

const CONSENSUS_TONE = {
  no_reviewers:     'border-ndor-edge bg-ndor-surface-alt/60 text-ndor-ink-muted',
  incomplete:       'border-amber-500/40 bg-amber-500/10 text-amber-200',
  all_agree:        'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  majority_advance: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  majority_reject:  'border-rose-500/40 bg-rose-500/10 text-rose-200',
  split:            'border-sky-500/40 bg-sky-500/10 text-sky-200',
};


export default function ReviewerCoveragePanel({ roleId }) {
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [accountId, setAccountId] = useState(null);
  const [active,    setActive]    = useState(0);
  const [pending,   setPending]   = useState(0);
  const [inReview,  setInReview]  = useState(0);
  const [decided,   setDecided]   = useState(0);
  const [consensus, setConsensus] = useState('no_reviewers');

  const load = useCallback(async () => {
    if (!roleId) return;
    setLoading(true);
    setError(null);
    try {
      const me  = await apiFetch('/api/employer/v1/me');
      const aid = me?.organisation?.id ?? me?.account_id ?? null;
      setAccountId(aid);
      if (!aid) {
        setActive(0); setPending(0); setInReview(0); setDecided(0);
        setConsensus('no_reviewers');
        return;
      }
      const [assignments, openReviews, decidedReviews] = await Promise.all([
        listReviewerAssignments(aid),
        listOpenReviewsByRole(aid, roleId),
        listDecidedReviewsByRole(aid, roleId),
      ]);
      const activeForRole = (assignments || []).filter(
        (a) => a.role_id === roleId && a.assignment_status === 'active',
      ).length;
      const p  = (openReviews || []).filter((r) => r.status === 'pending').length;
      const ir = (openReviews || []).filter((r) => r.status === 'in_review').length;
      const dec = (decidedReviews || []).length;
      const adv = (decidedReviews || []).filter((r) => r.decision === 'advance').length;
      const rej = (decidedReviews || []).filter((r) => r.decision === 'reject').length;
      const routedTotal = p + ir + dec;
      setActive(activeForRole);
      setPending(p);
      setInReview(ir);
      setDecided(dec);
      setConsensus(classifyConsensus({
        routed: routedTotal, decided: dec, advance: adv, reject: rej,
      }));
    } catch (e) {
      setError(e?.message || t('coverage.fail'));
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => { load(); }, [load]);

  const hasReviewers = active > 0;
  const showConsensus = (pending + inReview + decided) > 0;

  return (
    <Card data-testid="reviewer-coverage-panel">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Users className="h-4 w-4" />
          {t('coverage.title')}
        </CardTitle>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={loading}
          data-testid="reviewer-coverage-refresh"
          aria-label="Refresh reviewer coverage"
        >
          {loading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {error && (
          <div
            className="flex items-start gap-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-2 text-xs text-rose-200"
            data-testid="reviewer-coverage-error"
          >
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading && !error ? (
          <div
            className="flex items-center gap-2 text-xs text-slate-300"
            data-testid="reviewer-coverage-loading"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {t('coverage.loading')}
          </div>
        ) : (
          <>
            <dl className="grid grid-cols-4 gap-2 text-center">
              <Stat
                testId="reviewer-coverage-active"
                label={t('coverage.active_assignments')}
                value={active}
              />
              <Stat
                testId="reviewer-coverage-pending"
                label={t('coverage.open.pending')}
                value={pending}
              />
              <Stat
                testId="reviewer-coverage-in-review"
                label={t('coverage.open.in_review')}
                value={inReview}
              />
              <Stat
                testId="reviewer-coverage-decided"
                label={t('coverage.decided')}
                value={decided}
              />
            </dl>

            {showConsensus && (
              <div
                className={`rounded-md border px-3 py-2 text-xs ${CONSENSUS_TONE[consensus] || CONSENSUS_TONE.no_reviewers}`}
                data-testid="reviewer-coverage-consensus"
                data-consensus={consensus}
              >
                <span className="uppercase tracking-wide text-[10px] opacity-80">
                  {t('coverage.consensus.label')}
                </span>
                <span className="ml-1.5">{CONSENSUS_LABEL[consensus] || consensus}</span>
              </div>
            )}

            {!hasReviewers ? (
              <div
                className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200"
                data-testid="reviewer-coverage-empty"
              >
                {t('coverage.empty')}
              </div>
            ) : (
              <p
                className="text-xs text-slate-300"
                data-testid="reviewer-coverage-help"
              >
                {t('coverage.help')}
              </p>
            )}

            <Button
              size="sm"
              variant={hasReviewers ? 'outline' : 'default'}
              asChild
              className="w-full"
              data-testid="reviewer-coverage-cta"
              disabled={!accountId}
            >
              <Link
                to="/employer/reviewers"
                aria-label={hasReviewers ? t('coverage.cta.view_roster') : t('coverage.cta.assign')}
              >
                {hasReviewers ? t('coverage.cta.view_roster') : t('coverage.cta.assign')}
                <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}


function Stat({ testId, label, value }) {
  return (
    <div
      className="rounded-md border border-ndor-edge bg-ndor-bg/40 px-2 py-2"
      data-testid={testId}
    >
      <div className="text-xl font-semibold tabular-nums text-ndor-ink">
        {value}
      </div>
      <div className="mt-0.5 text-[10px] uppercase tracking-wide text-slate-300">
        {label}
      </div>
    </div>
  );
}
